import { Directive, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { NgModel } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, skip } from 'rxjs/operators';

/**
 *
 * NgModel Change Debounced Directive
 *
 * In AngularJS, we could easily use ng-model-options to pass in a debounce time (i.e. how long to wait after the user
 * stops typing to process their input). Unfortunately this feature no longer exists in Angular 9+ so we are creating
 * our own directive that handles this for us.
 *
 * If you need to add a debounce time to NgModelChange, use this directive instead.
 *
 * Input Properties:
 *  debounceTime: How long to debounce for in ms (optional)
 *
 * Output Properties:
 *  debouncedChangeHandler: The handler to call after the NgModel has changed
 *
 */

@Directive({
    standalone: false,
    selector: '[ycnNgModelChangeDebounced]',
})
export class NgModelChangeDebouncedDirective implements OnDestroy {
    @Input() debounceTime: number = 500;

    @Output() debouncedChangeHandler = new EventEmitter<void>();

    subscription: Subscription;

    constructor(private ngModel: NgModel) {
        this.subscription = this.ngModel.control.valueChanges
            .pipe(
                skip(1), // skip initial value (when the NgModel is first initialized with an empty value)
                distinctUntilChanged(),
                debounceTime(this.debounceTime)
            )
            .subscribe(() => {
                // In our only current use case (invite-friend-modal.template.html), we do need to use the value from
                // the subscription. However if necessary we could easily emit the value.
                this.debouncedChangeHandler.emit();
            });
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
